import { maxValue, minValue, required } from "ra-core";
import { useWatch } from "react-hook-form";

import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import {
  FINANCE_ADJUSTMENT_INDEXES,
  FINANCE_CONTRACT_STATUSES,
  toChoices,
} from "../financeOptions";

const statusChoices = toChoices(
  FINANCE_CONTRACT_STATUSES,
  "crm.finance.contracts.statuses",
);
const adjustmentChoices = toChoices(
  FINANCE_ADJUSTMENT_INDEXES,
  "crm.finance.contracts.adjustment_indexes",
);
const MONTH_NUMBERS = Array.from({ length: 12 }, (_, index) => index + 1);
const monthChoices = MONTH_NUMBERS.map((id) => ({
  id,
  name: `crm.finance.months.${id}`,
}));

/** Deals of the selected company only */
const DealInput = () => {
  const companyId = useWatch({ name: "company_id" });
  return (
    <ReferenceInput
      source="deal_id"
      reference="deals"
      filter={companyId ? { company_id: companyId } : {}}
      sort={{ field: "name", order: "ASC" }}
    >
      <SelectInput
        label="crm.finance.contracts.fields.deal"
        optionText="name"
        emptyText="crm.finance.none"
        helperText={false}
      />
    </ReferenceInput>
  );
};

const AdjustmentMonthInput = () => {
  const index = useWatch({ name: "adjustment_index" });
  if (!index || index === "none") return null;
  return (
    <SelectInput
      source="adjustment_month"
      label="crm.finance.contracts.fields.adjustment_month"
      choices={monthChoices}
      validate={required()}
      helperText={false}
    />
  );
};

/** Fields of the contract form */
export const ContractInputs = () => (
  <div className="flex flex-col gap-4">
    <TextInput
      source="name"
      label="crm.finance.contracts.fields.name"
      validate={required()}
      helperText={false}
      autoFocus
    />
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <ReferenceInput
        source="company_id"
        reference="companies"
        sort={{ field: "name", order: "ASC" }}
      >
        <AutocompleteInput
          label="crm.finance.fields.company"
          optionText="name"
          validate={required()}
          helperText={false}
          modal
        />
      </ReferenceInput>
      <DealInput />
      <NumberInput
        source="setup_amount"
        label="crm.finance.contracts.fields.setup_amount"
        min={0}
        step={0.01}
        validate={[required(), minValue(0)]}
        helperText="crm.finance.contracts.fields.setup_amount_help"
      />
      <NumberInput
        source="setup_installments"
        label="crm.finance.contracts.fields.setup_installments"
        min={1}
        step={1}
        validate={[required(), minValue(1), maxValue(36)]}
        helperText={false}
      />
      <NumberInput
        source="monthly_amount"
        label="crm.finance.contracts.fields.monthly_amount"
        min={0}
        step={0.01}
        validate={[required(), minValue(0)]}
        helperText={false}
      />
      <NumberInput
        source="billing_day"
        label="crm.finance.contracts.fields.billing_day"
        min={1}
        max={28}
        step={1}
        validate={[required(), minValue(1), maxValue(28)]}
        helperText={false}
      />
      <DateInput
        source="start_date"
        label="crm.finance.contracts.fields.start_date"
        validate={required()}
        helperText={false}
      />
      <DateInput
        source="end_date"
        label="crm.finance.contracts.fields.end_date"
        helperText={false}
      />
      <SelectInput
        source="status"
        label="crm.finance.fields.status"
        choices={statusChoices}
        validate={required()}
        helperText={false}
      />
      <SelectInput
        source="adjustment_index"
        label="crm.finance.contracts.fields.adjustment_index"
        choices={adjustmentChoices}
        validate={required()}
        helperText={false}
      />
      <AdjustmentMonthInput />
    </div>
    <TextInput
      source="notes"
      label="crm.finance.fields.notes"
      multiline
      helperText={false}
    />
  </div>
);
